import { msg } from "@lit/localize";
import { createAsyncAction, createAsyncActionGroup } from "@progressive-development/pd-spa-helper";
import type { startseite } from "@progressive-development/politik-model";

import type { StartseiteVerzeichnisse } from "../service/startseite-service.js";

/**
 * Async-Actions der Startseite — zwei, weil zwei Lebensdauern.
 *
 * Die Verzeichnisse ändern sich höchstens jährlich und werden einmal je Besuch
 * geholt; der Aufmacher wechselt, sobald jemand ein anderes Gebiet merkt.
 * Zusammengelegt zöge jeder Gebietswechsel 152 KB Gebiete erneut nach.
 *
 * Beide sind read-only — die Dokumente entstehen im Export der Pipeline.
 * Registriert in `common/store/app-loading-effects.ts` (backgroundGroups): Die
 * Startseite soll sich aufbauen, nicht hinter einem Modal warten.
 */
export const loadVerzeichnisse = createAsyncAction<StartseiteVerzeichnisse>("LOAD_VERZEICHNISSE", {
  loadingTxt: msg("Startseite wird geladen…", { id: "app.start.loading.toast" }),
  smallBackground: true,
});

/**
 * Der Aufmacher eines Gebiets.
 *
 * Die Action trägt den BEZIRKS-Slug, nicht den gemerkten: Aufmacher gibt es
 * nur je Bezirk (auf Stadtteil-Ebene sind Drucksachen und Termine zu dünn).
 * Den Bezirk trägt der Merker selbst mit (`GebietsAuswahl.bezirkSlug`) — es
 * wird nichts aus einem Namen erschlossen.
 *
 * Aufgelöst wird VOR dem Dispatch (`aufmacherSlug()` in `startseite-bezug.ts`):
 * So gibt es nur einen Ort, an dem „kein Merker" zu „stadtweit" wird.
 */
export const loadAufmacher = createAsyncActionGroup<
  /** Bezirks-Slug oder `startseite.AUFMACHER_STADTWEIT` — nie leer. */
  string,
  AufmacherErgebnis
>("LOAD_AUFMACHER", {
  loadingTxt: msg("Gebiet wird geladen…", { id: "app.start.aufmacher.loading.toast" }),
  smallBackground: true,
});

/**
 * Das Ergebnis trägt mit, WOFÜR geladen wurde.
 *
 * Ohne dieses Feld könnte die Seite nicht sagen, auf welches Gebiet sich die
 * Listen beziehen — und ein spät eintreffendes Ergebnis eines vorherigen
 * Gebiets wäre nicht als solches erkennbar.
 */
export interface AufmacherErgebnis {
  /** Bezirks-Slug oder `startseite.AUFMACHER_STADTWEIT`. */
  fuer: string;
  dokument?: startseite.AufmacherDokument;
}
